import { createContext, useContext, type ReactNode } from 'react';
import { Button, Space } from '@arco-design/web-react';

/** 设置页的页头操作集（保存出口的唯一形状：设置页壳与设置 Landing 共用） */
export interface SettingsHeaderActions {
  onSave: () => void;
  onReset?: () => void;
  dirty?: boolean;
  saving?: boolean;
  /** 主操作文案档：false =「保存」（仅落库，生效方式由卡头 effect 胶囊说明） */
  immediate?: boolean;
  /**
   * 保存按钮置灰（缺写能力时由设置页壳置真）。
   * 置灰只是「点不动」：**缺什么权限必须同时在页面上写出来**，只挂 title 用户看不见；
   * `disabledReason` 只是鼠标悬浮的兜底。
   */
  disabled?: boolean;
  /** 置灰原因的悬浮兜底文案（可见说明由页面正文承担） */
  disabledReason?: string;
}

/**
 * 页头操作插槽：值为 `null`（未提供 Provider）表示当前上下文没有插槽——
 * 独立设置页自己渲染操作区；设置 Landing 则提供一个插槽，让**当前激活 Tab** 把自己的
 * 保存出口注册到 Landing 的吸顶页头上（`register(null)` 表示注销）。
 */
const SlotContext = createContext<((actions: SettingsHeaderActions | null) => void) | null>(null);

export function SettingsHeaderSlotProvider({
  register,
  children,
}: {
  register: (actions: SettingsHeaderActions | null) => void;
  children: ReactNode;
}) {
  return <SlotContext.Provider value={register}>{children}</SlotContext.Provider>;
}

export function useSettingsHeaderSlot() {
  const register = useContext(SlotContext);
  return register;
}

/**
 * 页头操作区渲染：独立设置页的页头与设置 Landing 的页头共用同一实现，
 * 两处各写一份必然漂移（文案/禁用的条件/未保存提示的位置）。
 */
export function SettingsHeaderActionsView({ actions }: { actions: SettingsHeaderActions }) {
  return (
    <Space>
      {actions.dirty ? <span className="bcc-set-dirty">有未保存的修改</span> : null}
      {actions.onReset ? (
        <Button onClick={actions.onReset} disabled={actions.saving || !actions.dirty}>
          重置
        </Button>
      ) : null}
      <Button
        type="primary"
        loading={actions.saving}
        onClick={actions.onSave}
        disabled={actions.disabled}
        title={actions.disabled ? actions.disabledReason : undefined}
      >
        {actions.immediate === false ? '保存' : '保存并生效'}
      </Button>
    </Space>
  );
}
